/**
 * A4: promise-to-pay / contact / dispute form. Creates a staff-visible record;
 * never changes a balance. RHF + Zod per frontend rules.
 */

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { customerPortalApi, PortalRequestType } from '../../../services/customerPortalApi';
import { PORTAL_COPY, PortalLanguage } from '../portalCopy';

interface Props {
  accountUuid: string;
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  language?: PortalLanguage;
  initialType?: PortalRequestType;
}

const requestSchema = z
  .object({
    request_type: z.enum(['contact', 'dispute', 'promise_to_pay']),
    subject: z.string().min(1).max(200),
    message: z.string().min(1).max(2000),
    proposed_promise_date: z.string().optional(),
  })
  .refine((v) => v.request_type !== 'promise_to_pay' || !!v.proposed_promise_date, {
    message: 'Promise date is required',
    path: ['proposed_promise_date'],
  });

type RequestForm = z.infer<typeof requestSchema>;

const RequestModal: React.FC<Props> = ({
  accountUuid,
  open,
  onClose,
  onSubmitted,
  language = 'en',
  initialType,
}) => {
  const copy = PORTAL_COPY[language];
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RequestForm>({
    resolver: zodResolver(requestSchema),
    defaultValues: { request_type: initialType ?? 'contact', subject: '', message: '' },
  });
  const requestType = watch('request_type');

  useEffect(() => {
    if (open) {
      reset({ request_type: initialType ?? 'contact', subject: '', message: '' });
    }
  }, [open, initialType, reset]);

  if (!open) return null;

  const onSubmit = async (values: RequestForm) => {
    await customerPortalApi.createRequest({
      request_type: values.request_type as PortalRequestType,
      subject: values.subject,
      message: values.message,
      account_id: accountUuid,
      proposed_promise_date:
        values.request_type === 'promise_to_pay' ? values.proposed_promise_date : undefined,
    });
    reset();
    onSubmitted();
    onClose();
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">{copy.requestsNew}</h3>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          {requestType === 'promise_to_pay' ? (
            <p className="text-sm font-medium text-gray-700">{copy.requestsPromiseCta}</p>
          ) : (
            <label className="block text-sm text-gray-700">
              {copy.requestTypeLabel}
              <select
                {...register('request_type')}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="contact">{copy.requestTypeContact}</option>
                <option value="dispute">{copy.requestTypeDispute}</option>
              </select>
            </label>
          )}
          <label className="block text-sm text-gray-700">
            {copy.requestSubject}
            <input
              {...register('subject')}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            {errors.subject && (
              <span className="text-xs text-red-600">{errors.subject.message}</span>
            )}
          </label>
          <label className="block text-sm text-gray-700">
            {copy.requestMessage}
            <textarea
              rows={4}
              {...register('message')}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            {errors.message && (
              <span className="text-xs text-red-600">{errors.message.message}</span>
            )}
          </label>
          {requestType === 'promise_to_pay' && (
            <label className="block text-sm text-gray-700">
              {copy.requestPromiseDate}
              <input
                type="date"
                min={today}
                {...register('proposed_promise_date')}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              {errors.proposed_promise_date && (
                <span className="text-xs text-red-600">{errors.proposed_promise_date.message}</span>
              )}
            </label>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {copy.requestCancel}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {copy.requestSubmit}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestModal;
